import { utils } from 'ethers';
import { ArnAuthStatus, ArnConnectionStatus } from './ArnAuthStatus';
import { ArnClientError } from '../ArnClientError';
import { ArnAuthContext } from './ArnAuthContext';
import { ArnConnectorFactory } from './connector/ArnConnectorFactory';
import { BehaviorSubject, map, Observable } from 'rxjs';
import {
  ArnConnector,
  ArnConnectorType,
  ArnToken,
  ArnValidAuthConfig,
  AuthScheme,
  EthereumSig,
  JWT,
} from '@arianee/arn-types';
import { ChainId } from '../ChainId';
import { ArnHttpService } from '../http';
import { ArnAuthService } from './ArnAuthService';
import { ArnLogger } from '../log';

export class DefaultArnAuthService implements ArnAuthService {
  static readonly QUERY_PARAM_ARIANEE_ACCESS_TOKEN = 'arianeeAccessToken';

  /**
   * @deprecated This key should not be used anymore. It is kept for backward compatibility.
   */
  protected static readonly LEGACY_AUTH_CACHE_KEY = 'wcSessionSigned';
  /**
   * @deprecated This key should not be used outside the cleanWalletConnectInternalCache method.
   */
  protected static readonly WC_CACHE_KEY = 'walletconnect';

  protected _currentContext: ArnAuthContext | undefined = undefined;
  protected _currentContext$ = new BehaviorSubject<ArnAuthContext | undefined>(
    this._currentContext
  );

  get currentContext$(): Observable<ArnAuthContext | undefined> {
    return this._currentContext$;
  }

  get currentContext(): ArnAuthContext | undefined {
    return this._currentContext;
  }

  readonly status$ = this.currentContext$.pipe(map((c) => c?.status)); // new BehaviorSubject<ArnAuthStatus>(ArnAuthContext.DEFAULT_STATUS);

  protected readonly log: ArnLogger;

  constructor(
    readonly config: ArnValidAuthConfig,
    readonly connectorFactory: ArnConnectorFactory,
    log: ArnLogger,
    readonly http: ArnHttpService
  ) {
    this.log = log.fork('auth');
  }

  isConnected(context: ArnAuthContext | undefined = this._currentContext) {
    return (
      !!context &&
      context.status.connectionStatus !== ArnConnectionStatus.disconnected
    );
  }

  connectFromUrl(url: URL): ArnToken {
    const aat = url.searchParams.get(
      DefaultArnAuthService.QUERY_PARAM_ARIANEE_ACCESS_TOKEN
    );
    if (!aat) {
      throw new ArnClientError(
        `No ${DefaultArnAuthService.QUERY_PARAM_ARIANEE_ACCESS_TOKEN} value in ${url}`
      );
    }
    return this.connectFromToken(aat);
  }

  connectFromToken(aat: string): ArnToken {
    ArnAuthContext.asDisconnected(this._currentContext);
    const parsed = ArnToken.fromString(aat);
    const address = parsed.address;
    if (parsed.address !== '') {
      const connector = this.connectorFactory.create(ArnConnectorType.AAT, {});
      this.context = this.createContext(connector, {
        connectionStatus: ArnConnectionStatus.authenticated,
        address,
        message: undefined,
        signature: undefined,
        aat,
        connectorType: ArnConnectorType.AAT,
        connectorOpts: undefined,
      });
    }
    return parsed;
  }

  connectFromCache() {
    ArnAuthContext.asDisconnected(this._currentContext);
    const cachedAuthStatus = this.getAuthStatusCache();
    if (cachedAuthStatus) {
      const signer = this.getSigner(cachedAuthStatus.signature!);
      if (!signer) {
        throw new Error('Invalid signature');
      }
      const connector = this.connectorFactory.create(
        cachedAuthStatus.connectorType,
        cachedAuthStatus.connectorOpts
      );
      this.context = this.createContext(connector, {
        ...cachedAuthStatus,
        connectionStatus: ArnConnectionStatus.authenticated,
      });
      connector.onConnectFromCache();
    }
  }

  async connect(
    params: {
      authConfig: ArnValidAuthConfig;
      assignCurrentContext: boolean;
    } = { authConfig: this.config, assignCurrentContext: true }
  ): Promise<ArnAuthContext> {
    const { assignCurrentContext, authConfig } = params;
    if (assignCurrentContext) {
      ArnAuthContext.asDisconnected(this._currentContext);
    }
    const { connectorType, options } = authConfig;
    const connector = this.connectorFactory.create(connectorType, options);
    const persistConnectorState = Boolean(assignCurrentContext);
    const context = this.createContext(
      connector,
      {
        connectionStatus: ArnConnectionStatus.connecting,
        address: undefined,
        message: undefined,
        signature: undefined,
        aat: undefined,
        connectorType,
        connectorOpts: options,
      },
      persistConnectorState
    );
    if (assignCurrentContext) {
      this.context = context;
    }
    try {
      await this._connect(connector, context, authConfig.sigMessage);
    } catch (err) {
      context.status = {
        ...context.status,
        connectionStatus: ArnConnectionStatus.disconnected,
      };
      if (!((err as Error) instanceof ArnClientError)) {
        throw err;
      }
    }
    return context;
  }

  /**
   *
   * @param connector
   * @param context
   * @param message The message to sign.
   * @protected
   */
  protected async _connect(
    connector: ArnConnector,
    context: ArnAuthContext,
    message: string
  ) {
    await connector.connect();
    context.status = {
      ...context.status,
      connectionStatus: ArnConnectionStatus.signing,
    };
    const signature = await connector.signMessage(message);
    const address = this.getSigner(signature);
    context.status = {
      ...context.status,
      address,
      message,
      signature,
      connectionStatus: ArnConnectionStatus.authenticated,
    };
  }

  async connectStandalone(
    params: { authConfig: ArnValidAuthConfig } = { authConfig: this.config }
  ): Promise<ArnAuthContext> {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve) => {
      const { authConfig } = params;
      const { connectorType, options } = authConfig;
      const connector = this.connectorFactory.create(connectorType, options);
      const persistConnectorState = false;
      const context = this.createContext(
        connector,
        {
          connectionStatus: ArnConnectionStatus.connecting,
          address: undefined,
          message: undefined,
          signature: undefined,
          aat: undefined,
          connectorType,
          connectorOpts: options,
        },
        persistConnectorState
      );
      resolve(context);

      try {
        await this._connect(connector, context, authConfig.sigMessage);
      } catch (err) {
        console.error(err);
        context.status = {
          ...context.status,
          connectionStatus: ArnConnectionStatus.disconnected,
        };
      }
    });
  }

  protected set context(newContext: ArnAuthContext | undefined) {
    this._currentContext = newContext;
    this._currentContext$.next(this._currentContext);
  }

  protected createContext(
    connector: ArnConnector,
    newStatus: ArnAuthStatus,
    persist = true
  ): ArnAuthContext {
    const newContext = new ArnAuthContext(
      this.log,
      connector,
      newStatus,
      persist
    );
    newContext.status$.subscribe((newStatus) => {
      if (newStatus.connectionStatus === ArnConnectionStatus.authenticated) {
        // Applying post connection actions after a slight delay to let connectors finish their work
        setTimeout(() => {
          if (newContext.isConnected()) {
            const { trySwitchChainNumber } = this.config || {};
            if (trySwitchChainNumber && trySwitchChainNumber > 0) {
              this.trySwitchChain(trySwitchChainNumber);
            } else {
              this.log.debug('No chain switch requested');
            }
          }
        }, 750);
      }
    });
    return newContext;
  }

  async trySwitchChain(
    chainId: number,
    context = this._currentContext
  ): Promise<void> {
    this.log.debug(`trySwitchChain(${ChainId[chainId]})`);
    const ctx = ArnAuthContext.asConnected(context);
    await ctx.switchChain(chainId);
  }

  async disconnect(context = this._currentContext) {
    const ctx = ArnAuthContext.asConnected(context);
    await ctx.disconnect();
    this.context = undefined;
  }

  generateAuthorizationHeader(context = this._currentContext): string {
    const ctx = ArnAuthContext.asConnected(context);
    const { message, signature, aat } = ctx.status;
    if (message && signature) {
      const ethSig: EthereumSig = { message, signature };
      const ethSigAsJson = JSON.stringify(ethSig);
      const ethSigAsBase64 = btoa(ethSigAsJson);
      return `${AuthScheme.EthereumSig} ${ethSigAsBase64}`;
    } else if (aat) {
      return `${AuthScheme.Bearer} ${aat}`;
    } else {
      throw new ArnClientError('Invalid auth status');
    }
  }

  // TODO: Should be an API of the Legacy connector.
  cleanLegacyAuthCache() {
    const legacyCache = localStorage.getItem(
      DefaultArnAuthService.LEGACY_AUTH_CACHE_KEY
    );
    if (legacyCache) {
      localStorage.removeItem(DefaultArnAuthService.LEGACY_AUTH_CACHE_KEY);
      this.cleanWalletConnectInternalCache();
    }
  }

  /**
   * Clean the internal cache of WalletConnect (used by internally by their sdk)
   */
  private cleanWalletConnectInternalCache() {
    localStorage.removeItem(DefaultArnAuthService.WC_CACHE_KEY);
  }

  protected getSigner(signature: string, config = this.config): string {
    return utils.recoverAddress(
      utils.hashMessage(config.sigMessage),
      signature
    );
  }

  private getAuthStatusCache(): ArnAuthStatus | undefined {
    const _authStatusCache =
      localStorage.getItem(
        ArnAuthContext.AUTH_CACHE_KEY + ArnConnectorType.Web3ModalV2
      ) ||
      localStorage.getItem(
        ArnAuthContext.AUTH_CACHE_KEY + ArnConnectorType.Web3Modal
      ) ||
      localStorage.getItem(
        ArnAuthContext.AUTH_CACHE_KEY + ArnConnectorType.LegacyWC
      );
    if (_authStatusCache && _authStatusCache !== '') {
      const authStatusCache = JSON.parse(_authStatusCache) as ArnAuthStatus;
      if (
        authStatusCache.signature !== undefined &&
        authStatusCache.signature !== ''
      ) {
        return authStatusCache;
      }
    }
    return undefined;
  }

  async signJwt(jwt: JWT, keyName: string, tokenId?: string): Promise<string> {
    if (!this.config.jwt) {
      throw new Error('Expected JWT signing configuration');
    }
    const payload: object = { keyName, jwt, tokenId };
    return this.http.sendAuthorizedJson(this, this.config.jwt.url, payload);
  }
}
